import React, { useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Grid, Box, Typography, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Chip, Card, CardHeader, CardContent, CardActions, Button, Divider } from '@mui/material';
import NoteIcon from '@mui/icons-material/Note';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import TranslateIcon from '@mui/icons-material/Translate';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import SpellcheckEditor from './SpellcheckEditor';
import TagTextField from './TagTextField';
import './TranslationRow.css';

const TranslationRow = ({
    globalIndex,
    originalText,
    translatedText,
    isRepeat,
    tagMap,
    isProjectOwner,
    sourceDirection,
    targetDirection,
    grammarErrors,
    hasNote,
    isApproved,
    confidenceScore,
    translationSource,
    onOriginalTextChange,
    onTranslationChange,
    onGrammarReplace, // Used by SpellcheckEditor
    onGrammarClick,   // Used by non-editable view
    highlightGrammarIssues, // Helper function passed down
    onToggleApproval,
    onTranslate,
    onAddAnnotation,
    onCopyToClipboard,
    onAddGlossaryTerm,
    onRowFocus
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const rowRef = useRef(null);

    const handleOpenMore = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMore = () => {
        setAnchorEl(null);
    };

    const headerAction = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {tagMap && tagMap._pageContext && (
                <Chip
                    label={`P${tagMap._pageContext}`}
                    size="small"
                    sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #e2e8f0'
                    }}
                />
            )}
            {isRepeat && (
                <Chip
                    label="RP"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                />
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, ml: 1 }}>
                #{globalIndex + 1}
            </Typography>
        </Box>
    );

    return (
        <Grid item xs={12}>
            <Box
                ref={rowRef}
                className="segment-card-row"
                sx={{ 
                    position: 'relative', 
                    mb: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)'
                    }
                }}
                onFocusCapture={() => onRowFocus && onRowFocus(globalIndex)}
            >
                <Grid container spacing={2}>
                    {/* Source Text Card */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '12px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: 'white'
                        }}>
                            <CardHeader
                                title="Source Text"
                                titleTypographyProps={{ variant: 'subtitle2', fontWeight: 700, color: '#64748b' }}
                                action={headerAction}
                                sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}
                            />
                            <CardContent sx={{ p: 2, flexGrow: 1 }}>
                                {isProjectOwner ? (
                                    <SpellcheckEditor
                                        dir={sourceDirection}
                                        tagMap={tagMap}
                                        style={{
                                            minHeight: 100,
                                            border: 'none',
                                            padding: 0,
                                            textAlign: sourceDirection === 'rtl' ? 'right' : 'left',
                                            fontSize: '0.95rem',
                                            lineHeight: 1.6
                                        }}
                                        value={originalText}
                                        onChange={newText => onOriginalTextChange(globalIndex, newText)}
                                        errors={grammarErrors || []}
                                        onReplace={(sug, errIdx) => onGrammarReplace && onGrammarReplace(globalIndex, sug, errIdx)}
                                        readOnly={false}
                                    />
                                ) : (
                                    <Box
                                        sx={{ 
                                            minHeight: 100, 
                                            whiteSpace: 'pre-wrap', 
                                            textAlign: sourceDirection === 'rtl' ? 'right' : 'left',
                                            fontSize: '0.95rem',
                                            lineHeight: 1.6
                                        }}
                                        dir={sourceDirection}
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightGrammarIssues ? highlightGrammarIssues(globalIndex, originalText, grammarErrors || []) : originalText) }}
                                        onClick={onGrammarClick}
                                    />
                                )}
                            </CardContent>
                            <Divider sx={{ borderColor: '#f1f5f9' }} />
                            <CardActions sx={{ px: 2, py: 1, justifyContent: 'flex-end', gap: 1 }}>
                                <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', textTransform: 'none', fontSize: '0.75rem' }}>Split</Button>
                                <Button 
                                    size="small" 
                                    variant="text" 
                                    startIcon={<NoteIcon sx={{ fontSize: 16 }} />} 
                                    onClick={() => onAddAnnotation(globalIndex)}
                                    sx={{ color: hasNote ? '#8b5cf6' : '#64748b', textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    {hasNote ? 'Edit Note' : 'Add Note'}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>

                    {/* Translated Text Card */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ 
                            border: '1px solid',
                            borderColor: isApproved ? '#22c55e' : '#e2e8f0',
                            borderRadius: '12px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: isApproved ? '#f0fdf4' : 'white',
                            position: 'relative',
                            transition: 'all 0.2s ease'
                        }}>
                            <CardHeader
                                title="Translated Text"
                                titleTypographyProps={{ variant: 'subtitle2', fontWeight: 700, color: '#64748b' }}
                                action={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {translationSource && (
                                            <Chip
                                                label={translationSource === 'memory' ? 'Memory' : 'AI'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 18, fontSize: '0.65rem', color: '#64748b' }}
                                            />
                                        )}
                                        {confidenceScore !== undefined && (
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: confidenceScore > 80 ? '#22c55e' : '#f59e0b' }}>
                                                {Math.round(confidenceScore)}%
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}
                            />
                            <CardContent sx={{ p: 2, flexGrow: 1 }}>
                                <TagTextField
                                    value={translatedText || ''}
                                    tagMap={tagMap}
                                    onChange={v => onTranslationChange(globalIndex, v)}
                                    dir={targetDirection}
                                    style={{
                                        textAlign: targetDirection === 'rtl' ? 'right' : 'left',
                                        backgroundColor: 'transparent',
                                        minHeight: 100,
                                        border: 'none',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        fontSize: '0.95rem',
                                        lineHeight: 1.6
                                    }}
                                    disabled={isApproved}
                                />
                            </CardContent>
                            <Divider sx={{ borderColor: isApproved ? '#dcfce7' : '#f1f5f9' }} />
                            <CardActions sx={{ px: 2, py: 1, justifyContent: 'flex-end', gap: 1 }}>
                                <IconButton size="small" onClick={handleOpenMore} sx={{ color: '#64748b' }}>
                                    <MoreVertIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <Button 
                                    size="small" 
                                    variant="outlined" 
                                    startIcon={isApproved ? <UndoIcon sx={{ fontSize: 16 }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => onToggleApproval(globalIndex)}
                                    color={isApproved ? "warning" : "success"}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px' }}
                                >
                                    {isApproved ? 'Unapprove' : 'Approve'}
                                </Button>
                                {isProjectOwner && !isApproved && (
                                    <Button 
                                        size="small" 
                                        variant="contained" 
                                        disableElevation
                                        startIcon={<TranslateIcon sx={{ fontSize: 16 }} />} 
                                        onClick={() => onTranslate(globalIndex)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', bgcolor: '#3b82f6' }}
                                    >
                                        Translate
                                    </Button>
                                )}
                            </CardActions>
                        </Card>
                    </Grid>
                </Grid>

                {/* More Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMore}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            mt: 1,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            border: '1px solid #f1f5f9'
                        }
                    }}
                >
                    <MenuItem onClick={() => { onCopyToClipboard(translatedText); handleCloseMore(); }}>
                        <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Copy Translation" />
                    </MenuItem>
                    <MenuItem onClick={() => { onAddGlossaryTerm(globalIndex); handleCloseMore(); }}>
                        <ListItemIcon><LibraryBooksIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Add to Glossary" />
                    </MenuItem>
                </Menu>
            </Box>
        </Grid>
    );
};

export default TranslationRow;
